import { ContractItem } from '@/data/schema/contract-item'
import type { ContractItemStatus } from '@/data/schema/contract-item-status'
import type { WorkspaceArea } from '@/data/types/area'
import { contractItemStatusMap } from './statusMaps'
import { statusParam } from '@/composables/contracts'
import type { CollectionItem } from '@/data/schema/collection-item'
import { JourneyItem } from '@/data/schema/journey-item'
import { cloneDeep } from 'lodash'

/**
 * Pass in Items array, return all items and kit components
 * @param items ContractItem[]
 * @returns ContractItem[]
 */
export function getAllItems(items: ContractItem[] | JourneyItem[]) {
	const allItems = []

	items.forEach((item) => {
		allItems.push(item)

		if (item.type == 'Kit' && item.kit_components) {
			item.kit_components.forEach((kit: ContractItem) => {
				allItems.push(kit)
			})
		}
	})

	return allItems
}

export function getPickingList(
	items: ContractItem[],
	includeNonCount: boolean = true,
	includeStatuses?: string[]
): ContractItem[] {
	console.log(includeStatuses)
	const clonedItems = cloneDeep(items)
	let pickingList: ContractItem[] = []

	clonedItems.forEach((item) => {
		if (item.type === 'Kit' && item.kit_components) {
			item.kit_components.forEach((accessory: ContractItem) => {
				const shouldInclude =
					!includeStatuses || includeStatuses.includes(accessory.status_description)
				if (shouldInclude) {
					pickingList = mergeQuantities(pickingList, accessory, item.quantity_required)
				}
			})
		} else {
			const shouldInclude =
				!includeStatuses || includeStatuses.includes(item.status_description)
			if (shouldInclude) {
				pickingList = mergeQuantities(pickingList, item)
			}
		}
	})

	pickingList = pickingList.filter((item) => !item.item_number.startsWith('ENG-'))

	if (!includeNonCount) {
		pickingList = pickingList.filter((item) => !item.description.endsWith('ZZ'))
	}

	pickingList.sort((a, b) => a.item_code.localeCompare(b.item_code))

	return pickingList
}

export function mergeQuantities(
	targetItems: ContractItem[],
	itemsToMerge: ContractItem | ContractItem[],
	multiplier: number = 1
): ContractItem[] {
	if (!(itemsToMerge instanceof Array)) {
		itemsToMerge = [itemsToMerge]
	}
	itemsToMerge.forEach((item) => {
		const existingItem = targetItems.find((val) => val.item_code === item.item_code)
		if (existingItem) {
			existingItem.quantity_required += item.quantity_required * multiplier
		} else {
			const newItem = Object.assign(item)
			newItem.quantity = item.quantity_required * multiplier
			targetItems.push(newItem)
		}
	})
	return targetItems
}

export function getConsolidatedItems(items: ContractItem[] | JourneyItem[]) {
	const allItems = []

	items.forEach((item) => {
		allItems.push(item)

		if (item.type == 'Kit' && item.kit_components) {
			item.kit_components.forEach((kit: ContractItem) => {
				allItems.push(kit)
			})
		}
	})

	return allItems
}

export function getAllCollectionItems(items: CollectionItem[]): CollectionItem[] {
	const allItems: CollectionItem[] = []

	items.forEach((item) => {
		allItems.push(item)

		if (item.type == 'Kit' && item.kit_components) {
			item.kit_components.forEach((kit: CollectionItem) => {
				allItems.push(kit)
			})
		}
	})

	return allItems
}

/**
 * Filter "changed" param from items and kit components
 * To be used for datatable to prevent data mismatch on selections
 * @param items
 * @returns ContractItem[]
 */
export function filterItems(items: ContractItem[] | CollectionItem[]) {
	return items.map((item) => {
		const newItem: { [key: string]: any } = {}

		for (const key in item) {
			if (key !== 'changed') {
				newItem[key] = (item as { [key: string]: any })[key]
			}
		}

		// Filter out "changed" key from kit components
		if (item.kit_components) {
			newItem.kit_components = item.kit_components.map((component: any) => {
				const newComponent: { [key: string]: any } = {}

				for (const key in component) {
					if (key !== 'changed') {
						newComponent[key] = component[key]
					}
				}

				return newComponent
			})
		}

		return newItem
	})
}

/**
 * Loop through all items and components and update a key:value pair
 * set valueIsParam to true to use another key value as the value
 * e.g set quantity_confirmed to the value of quantity_required
 * @param items ContractItem array
 * @param area WorkspaceArea
 * @param key keyof ContractItemStatus
 * @param value string - either a new value or a status key
 * @param valueIsParam boolean - false by default
 * @returns ContractItem[]
 */
export function updateStatusParamOnItems<ContractItem extends Record<string, any>>(
	items: ContractItem[],
	area: WorkspaceArea,
	key: keyof ContractItemStatus,
	value: string | keyof ContractItemStatus,
	valueIsParam = false
): ContractItem[] {
	const statusKey = contractItemStatusMap[area]

	items.forEach((item) => {
		if (Object.keys(item[statusKey][0]).includes(key)) {
			item[statusKey][0][key] = valueIsParam ? item[statusKey][0][value] : value
		}

		if (item.type == 'Kit' && item.kit_components) {
			item.kit_components.forEach((component: ContractItem) => {
				if (Object.keys(component[statusKey][0]).includes(key)) {
					component[statusKey][0][key] = valueIsParam
						? component[statusKey][0][value]
						: value
				}
			})
		}
	})

	return items
}

export function splitWeightByDepot(
	items: ContractItem[] | JourneyItem[],
	area: WorkspaceArea
): { [depot: string]: number } {
	const depotWeightMap = getAllItems(items)
		.map((item) => {
			const getDepot = () => {
				if (statusParam(item, 'depot', area)) {
					return statusParam(item, 'tos', area)
						? item.depot
						: statusParam(item, 'depot', area)
				} else {
					return item.depot
				}
			}

			const getWeight = () => {
				return statusParam(item, 'weight', area) || item.weight
			}

			const depot = getDepot()
			const weight = getWeight()

			return {
				depot: depot,
				weight: Number(weight)
			}
		})
		.filter(
			(item) =>
				item.depot !== undefined && typeof item.weight === 'number' && !isNaN(item.weight)
		)

	const weightByDepotMap: { [depot: string]: number } = {}

	depotWeightMap.forEach((item) => {
		if (!weightByDepotMap[item.depot]) {
			weightByDepotMap[item.depot] = 0
		}

		weightByDepotMap[item.depot] += item.weight
	})

	return weightByDepotMap
}
